/**
 * Tombol **Buka Laci** — fitur POS umum, dan sekaligus jalur pengambilan uang yang paling tak
 * meninggalkan jejak fisik.
 *
 * ### ⭐ Kenapa PIN diminta ULANG di layar ini
 * Sesi staf berumur 8 jam. Tanpa PIN ulang, tombol ini mewarisi seluruh umur sesi — dan siapa pun
 * yang menemukan terminal tak terkunci bisa membukanya berkali-kali atas nama kasir yang sedang
 * di belakang.
 *
 * ⚠️ Teks dialognya menyebut **alasan** PIN diminta lagi, bukan sekadar "masukkan PIN". Permintaan
 * yang tak dijelaskan akan terasa gangguan, dan yang terasa gangguan akan dicari jalan pintasnya.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';

/** Alasan yang ditawarkan di layar. ⛔ Enum kontrak, bukan teks bebas yang diketik kasir. */
const REASONS = ['make_change', 'cash_drop', 'personal_item', 'other'] as const;

export interface DrawerButtonProps {
  /** Sesi kas berjalan. `null` = belum ada sesi ⇒ tombol nonaktif. */
  readonly shiftId: string | null;
  readonly deviceCode: string;
}

export function DrawerButton({ shiftId, deviceCode }: DrawerButtonProps) {
  const { t } = useTranslation();
  const [openApp, setBuka] = useState(false);
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState<(typeof REASONS)[number]>('make_change');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ⛔ `other` tanpa penjelasan = teks bebas jilid dua. Layar menolak lebih dulu; server tetap jaring.
  const needsNote = reason === 'other' && note.trim() === '';
  const canOpen = pin.length >= 4 && !needsNote && !busy;

  const close = () => {
    setBuka(false);
    setPin('');
    setNote('');
    setError(null);
  };

  return (
    <>
      <button
        type="button"
        className="action-secondary"
        data-testid="open-drawer"
        disabled={shiftId === null}
        title={shiftId === null ? t('cashier.drawer.needsSession') : undefined}
        onClick={() => { setBuka(true); setMessage(null); }}
      >
        {t('cashier.drawer.button')}
      </button>

      {openApp && (
        <div className="dialog" role="dialog" aria-label={t('cashier.drawer.title')} data-testid="drawer-dialog">
          <h2>{t('cashier.drawer.title')}</h2>
          {/* ⭐ Menyebut ALASAN PIN diminta lagi — permintaan tak berpenjelasan terasa gangguan. */}
          <p className="dialog-note">{t('cashier.drawer.pinAgain')}</p>

          <label>
            {t('cashier.drawer.reason')}
            <select
              data-testid="drawer-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as (typeof REASONS)[number])}
            >
              {REASONS.map((a) => (
                <option key={a} value={a}>{t(`enum.reasonCode.${a}`)}</option>
              ))}
            </select>
          </label>

          {reason === 'other' && (
            <label>
              {t('cashier.drawer.note')}
              <input
                data-testid="drawer-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          )}

          <label>
            PIN
            <input
              type="password"
              inputMode="numeric"
              data-testid="drawer-pin"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </label>

          {error !== null && <p className="error" data-testid="drawer-error">{error}</p>}

          <div className="dialog-actions">
            <button type="button" data-testid="drawer-cancel" onClick={close}>
              {t('cashier.drawer.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="drawer-open"
              disabled={!canOpen}
              onClick={() => {
                if (shiftId === null) return;
                setBusy(true);
                setError(null);
                void api
                  .openDrawer({
                    shiftId,
                    reasonCode: reason,
                    ...(note.trim() === '' ? {} : { reasonNote: note.trim() }),
                    pin,
                    deviceCode,
                  })
                  .then((r) => {
                    close();
                    /*
                     * ⚠️ Kasir DIBERI TAHU bahwa kejadiannya ditandai untuk ditinjau. Kontrol yang
                     * diam-diam menandai orang adalah kontrol yang, saat ketahuan, dianggap jebakan.
                     */
                    setMessage(r.priorityReview ? t('cashier.drawer.reviewed') : t('cashier.drawer.ok'));
                  })
                  .catch((e: Error) => { setError(e.message); })
                  .finally(() => { setBusy(false); });
              }}
            >
              {t('cashier.drawer.open')}
            </button>
          </div>
        </div>
      )}

      {message !== null && <p className="info" data-testid="drawer-message">{message}</p>}
    </>
  );
}
